import { DateTime } from "luxon";

import i18n, { currentLanguage, luxonLocale } from ".";
import { formatNumber } from "../utilities/format/number";

export interface ServerMessage {
  key?: string | null;
  args?: Record<string, unknown> | null;
  text: string;
  parts?: ServerMessage[] | null;
  separator?: string | null;
}

export type Warning = string | ServerMessage;

export const isServerMessage = (value: unknown): value is ServerMessage =>
  !!value && typeof value === "object" && typeof (value as ServerMessage).text === "string" && ("key" in value || "parts" in value);

const asDate = (value: unknown, keepOffset = false) =>
  DateTime.isDateTime(value) ? value : typeof value === "string" ? DateTime.fromISO(value, keepOffset ? { setZone: true } : { zone: "utc" }) : null;

const written = (value: DateTime | null, format: string, raw: unknown) =>
  value?.isValid ? value.setLocale(luxonLocale(currentLanguage())).toFormat(format) : String(raw ?? "");

const NUMBER_FORMATS: Record<string, Intl.NumberFormatOptions> = {
  whole: { maximumFractionDigits: 0, useGrouping: false },
  upto1: { maximumFractionDigits: 1, useGrouping: false },
  upto2: { maximumFractionDigits: 2, useGrouping: false },
  upto3: { maximumFractionDigits: 3, useGrouping: false },
  fixed1: { minimumFractionDigits: 1, maximumFractionDigits: 1, useGrouping: false },
  n1: { minimumFractionDigits: 1, maximumFractionDigits: 1 },
};

let formatsAdded = false;

const addFormats = () => {
  const formatter = i18n.services?.formatter;
  if (formatsAdded || !formatter) return;
  formatter.add("date", (value: unknown) => written(asDate(value), "d MMM yyyy", value));
  formatter.add("time", (value: unknown) => written(asDate(value, true), "HH:mm d MMM", value));
  for (const [name, options] of Object.entries(NUMBER_FORMATS))
    formatter.add(name, (value: unknown) => (typeof value === "number" ? formatNumber(value, options) : String(value ?? "")));
  formatsAdded = true;
};

const inSentence = (label: string) => {
  const second = label.charAt(1);
  return second && second !== second.toLocaleLowerCase() ? label : label.charAt(0).toLocaleLowerCase(currentLanguage()) + label.slice(1);
};

const argText = (message: ServerMessage) =>
  message.key?.startsWith("enums.") ? inSentence(serverMessage(message)) : serverMessage(message);

export const serverMessage = (message: ServerMessage): string => {
  if (message.parts) return message.parts.map(serverMessage).join(message.separator ?? " ");
  if (!message.key) return message.text;

  addFormats();
  const args = Object.fromEntries(
    Object.entries(message.args ?? {}).map(([name, value]) => [name, isServerMessage(value) ? argText(value) : value]));
  return i18n.t(message.key, {
    ns: "server",
    replace: args,
    count: typeof args.count === "number" ? args.count : undefined,
    defaultValue: message.text,
  });
};

export const messageText = (message: ServerMessage | null | undefined) => (message ? serverMessage(message) : "");

export const warningText = (warning: Warning) => (typeof warning === "string" ? warning : serverMessage(warning));

type KeyedError = { description?: unknown; message?: unknown };

export function translateServerErrors(data: unknown) {
  const errors = data && typeof data === "object" ? (data as { errors?: unknown }).errors : undefined;
  if (!Array.isArray(errors)) return;

  for (const error of errors as (KeyedError | null)[])
    if (error && typeof error.description === "string" && isServerMessage(error.message))
      error.description = serverMessage(error.message);
}
